import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useTranslation } from "@refinedev/core";
import Button from "components/button";
import Input from "components/input";
import { Label } from "components/label";
import { IModalProps, Modal } from "components/modal";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import i18n from "utills/i18n";
import { z } from "zod";

interface AddTemplateModalProps extends IModalProps {
  defaultValue?: string;
  onSubmit: (data: string) => void;
}

const AddTemplateSchema = z.object({
  name: z.string().min(1, { message: i18n.t("validation.errorMessage.thisFieldIsRequired") }),
});

export const TemplateNameModal = ({
  open,
  onClose,
  defaultValue,
  onSubmit,
}: AddTemplateModalProps) => {
  const { translate } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<{ name: string }, HttpError, { name: string }>({
    defaultValues: {
      name: defaultValue ?? "",
    },
    resolver: zodResolver(AddTemplateSchema),
  });

  useEffect(() => {
    if (open) {
      reset({ name: defaultValue ?? "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const _submit = (values: { name: string }) => {
    onSubmit(values.name);
    onClose();
  };

  return (
    <Modal
      title={
        defaultValue
          ? translate(
              "eventSetup.participantTemplate.modal.templateName.editTemplateName"
            )
          : translate(
              "eventSetup.participantTemplate.modal.templateName.addTemplateName"
            )
      }
      onClose={onClose}
      open={open}
    >
      <div>
        <div className="w-full divide-y divide-gray-100 text-sm">
          <div className="box-container">
            <Label
              required
              id={`template_name`}
              name={translate(
                "eventSetup.participantTemplate.modal.templateName.templateName"
              )}
            />

            <Input
              label="Name"
              id="template_name"
              type="text"
              placeholder={translate(
                "eventSetup.participantTemplate.modal.templateName.templateName"
              )}
              error={errors["name"]}
              {...register("name")}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter") {
                  handleSubmit(_submit)();
                }
              }}
            />
            {errors["name"] ? (
              <p className="error-text">
                <span className="font-medium">{errors["name"].message}</span>
              </p>
            ) : null}
          </div>
          <div className="w-full box-container py-4">
            <div className="flex items-center justify-end gap-x-3">
              <Button
                className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                onClick={onClose}
              >
                {translate("button.cancel")}
              </Button>
              <Button className="w-20" onClick={handleSubmit(_submit)}>
                {defaultValue ? translate("button.save") : translate("button.add")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
